import { Formik } from "formik";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import * as yup from "yup";

import { PRODUCT_NAME } from "@src/shared/brand";
import { FormikInput } from "@src/shared/components/forms/FormikInput";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Logo } from "@src/shared/components/general/Logo";
import { Text } from "@src/shared/components/general/Text";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";
import { navigationV2 } from "@src/shared/navigation/v2";

import type { LoginPageV2Values } from "../../../../application";

export type { LoginPageV2Values };

export interface LoginPageV2Props {
  email?: string;
  password?: string;
  isSubmitting?: boolean;
  error?: string | null;
  onLoginWithElation?: (values: LoginPageV2Values) => void | Promise<void>;
  onSubmit?: (values: LoginPageV2Values) => void | Promise<void>;
}

const validationSchema = yup.object<LoginPageV2Values>({
  email: yup.string().email().required(),
  password: yup.string().required(),
});

export function LoginPageV2({
  email,
  password,
  isSubmitting = false,
  error = null,
  onLoginWithElation,
  onSubmit,
}: LoginPageV2Props) {
  return (
    <VerticalFlexbox
      as="main"
      alignItems="center"
      justifyContent="center"
      className="w-full bg-background px-[30px] py-11 text-foreground"
    >
      <Formik<LoginPageV2Values>
        enableReinitialize
        initialValues={{
          email: email ?? "",
          password: password ?? "",
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await onSubmit?.(values);
        }}
      >
        {({ handleSubmit, values }) => {
          return (
            <VerticalFlexbox
              as="form"
              alignItems="center"
              aria-label={`${PRODUCT_NAME.singular} login`}
              className="w-full max-w-[420px] text-center"
              onSubmit={handleSubmit}
            >
              <Logo variant="lockup" className="h-[58px]" />

              <HeaderText
                tag="h1"
                className="mt-[18px] max-w-[420px] text-balance text-[25px] leading-tight tracking-[-0.6px]"
              >
                Your care-gap-aware scribe
              </HeaderText>

              <Text
                as="p"
                appearance="subdued"
                sizing="sm"
                className="mt-[9px] max-w-[350px] text-balance leading-[1.55]"
              >
                Walk in knowing the visit&apos;s value-based gaps. Walk out with
                a note and the codes, ready to paste into your chart.
              </Text>

              <VerticalFlexbox
                className="mt-[26px] w-full max-w-[320px] space-y-[9px]"
                gap="none"
              >
                <ErrorMessage error={error} variant="boxed" />

                <FormikInput
                  aria-label="Email"
                  autoComplete="email"
                  className="h-[42px] rounded-[9px] border-input px-3 text-[13.5px] shadow-none placeholder:text-muted-foreground/70"
                  disabled={isSubmitting}
                  id="login-v2-email"
                  inputMode="email"
                  name="email"
                  placeholder="you@practice.com"
                  type="email"
                />

                <FormikInput
                  aria-label="Password"
                  autoComplete="current-password"
                  className="h-[42px] rounded-[9px] border-input px-3 text-[13.5px] shadow-none placeholder:text-muted-foreground/70"
                  disabled={isSubmitting}
                  id="login-v2-password"
                  name="password"
                  placeholder="Password"
                  type="password"
                />

                <Button
                  asChild
                  className="mt-[7px] h-[42px] w-full rounded-[9px] text-[13.5px] font-semibold"
                >
                  <Link to={navigationV2.signUp()}>
                    Get Started
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
                <Button
                  className="h-[42px] w-full rounded-[9px] text-[13.5px] font-semibold"
                  disabled={isSubmitting}
                  type="submit"
                  variant="outline"
                >
                  {isSubmitting ? <Spinner size="sm" /> : null}
                  Login
                </Button>
                {onLoginWithElation ? (
                  <Button
                    className="h-[42px] w-full rounded-[9px] text-[13.5px] font-semibold"
                    disabled={isSubmitting}
                    onClick={() => {
                      void onLoginWithElation(values);
                    }}
                    type="button"
                    variant="secondary"
                  >
                    Login with Elation
                  </Button>
                ) : null}
              </VerticalFlexbox>
            </VerticalFlexbox>
          );
        }}
      </Formik>
    </VerticalFlexbox>
  );
}
